import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {Box, Card, Container, IconButton, Skeleton, Stack, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
    Tooltip, Typography, useMediaQuery, useTheme} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContactsIcon from '@mui/icons-material/Contacts';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {useBusyCallback} from '../../util/react_hooks.js';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import api from '../../util/api.js';
import {useUser} from '../../util/user.js';
import {downloadBlob, formatTime, SIMONA_USER_ID} from '../../util/util.js';
import CONFIG from '../../config.js';
import {withTitle} from '../../title.js';
import withMainContainer from '../../components/WithMainContainer.js';
import {withPermGuard} from '../../components/PermGuard.js';
import RadioCards from '../../components/RadioCards.js';
import SCButton from '../../components/SCButton.js';
import SCTextField from '../../components/SCTextField.js';
import {withLoginRequire} from '../../components/Login.js';
import {closeModal, openModal} from '../../components/Modals.js';

const useStudentSettings = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['studentSettings'],
        queryFn: ()=>api.tarotmail.student.get(),
    });
    return [data?.studentSettings, isLoading];
};

const useCustomers = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['customers'],
        queryFn: ()=>api.tarotmail.student.customers(),
    });
    return [data?.customers, isLoading];
};

const EmailListTableHeader = props=>{
    let {children, orderable, order, changeOrder, id, ...rest} = props;
    let [showOrderButton, setShowOrderButton] = useState();
    return <TableCell align="right"
      onMouseEnter={()=>setShowOrderButton(true)}
      onMouseLeave={()=>setShowOrderButton(false)}
      {...rest}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography fontWeight="bold" marginTop={1} marginBottom={1}>
          {children}
        </Typography>
        {orderable&&(showOrderButton||order.column==id)&&<IconButton
          size="small" onClick={()=>changeOrder(id)}
          color={order.column==id ? 'primary' : undefined}>
          {order.column==id&&order.direction=='desc' ? <ArrowUpwardIcon/> : <ArrowDownwardIcon/>}
        </IconButton>}
      </Stack>
    </TableCell>;
};

const EmailListTableSkeleton = ()=><TableContainer component={Card}>
  <Table sx={{minWidth: 650}}>
    <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
      <TableRow>
        <EmailListTableHeader id="email">
          דוא"ל
        </EmailListTableHeader>
        <EmailListTableHeader id="name">
          שם
        </EmailListTableHeader>
        <EmailListTableHeader id="created">
          הצטרף ב
        </EmailListTableHeader>
        <EmailListTableHeader id="round" width={200}>
          סיבוב מסרים
        </EmailListTableHeader>
        <EmailListTableHeader width={50}>
          פעולות
        </EmailListTableHeader>
      </TableRow>
    </TableHead>
    <TableBody>
      {Array(3).fill(null).map((__, i)=><TableRow key={i}>
        {Array(5).fill(null).map((__, j)=><TableCell key={j} align="right">
          <Skeleton variant="rounded" width={200} height={30}/>
        </TableCell>)}
      </TableRow>)}
    </TableBody>
  </Table>
</TableContainer>;

const EmailListEmptyState = props=>{
    let {search} = props;
    let theme = useTheme();
    let IconComp = useMemo(()=>search?.length ? PersonSearchIcon : ContactsIcon, [search]);
    let text = useMemo(()=>search?.length
        ? `לא נמצאו משתמשים שתואמים לחיפוש "${search}"`
        : `כאן יופיעו המשתמשים שיתחברו לאפליקציה שלכם`, [search]);
    return <Stack width="100%" direction="column" gap={2} justifyContent="center" alignItems="center" padding={4}>
      <IconComp fontSize="large" sx={{color: theme.palette.text.disabled, height: '64px', width: '64px'}}/>
      <Typography textAlign="center" color="text.secondary">
        {text}
      </Typography>
    </Stack>;
}

const EmailListTable = props=>{
    let {loading, customers, search} = props;
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let [order, setOrder] = useState({});
    let changeOrder = useCallback(col=>setOrder(prev=>{
        if (prev.column!=col)
            return {column: col, direction: 'asc'};
        if (prev.direction=='asc')
            return {column: col, direction: 'desc'};
        return {};
    }), []);
    let orderedCustomers = useMemo(()=>{
        if (!customers?.length||_.isEmpty(order))
            return customers;
        return _.orderBy(customers, [order.column], [order.direction]);
    }, [customers, order]);
    let [addingRounds, setAddingRounds] = useState(new Set());
    let queryClient = useQueryClient();
    let {enqueueSnackbar} = useSnackbar();
    let addCustomerRound = useCallback(async customer=>{
        setAddingRounds(prev=>{
            let val = new Set(prev);
            val.add(customer.id);
            return val;
        });
        try {
            await api.tarotmail.student.addCustomerRound(customer.id);
            queryClient.invalidateQueries({queryKey: ['customers']});
            enqueueSnackbar(`אושר סבב מסרים נוסף עבור ${customer.name}`, {variant: 'success'});
        } catch (e) {
            enqueueSnackbar(e.message, {variant: 'error'});
        } finally {
            setAddingRounds(prev=>{
                let val = new Set(prev);
                val.delete(customer.id);
                return val;
            });
        }
    }, [queryClient, enqueueSnackbar]);
    if (loading)
        return <EmailListTableSkeleton/>;
    if (!orderedCustomers?.length&&sm)
        return <EmailListEmptyState search={search}/>;
    return <TableContainer component={Card}>
      <Table sx={{minWidth: 650}}>
        <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
          <TableRow>
            <EmailListTableHeader id="email" orderable
              order={order} changeOrder={changeOrder}>
              דוא"ל
            </EmailListTableHeader>
            <EmailListTableHeader id="name" orderable
              order={order} changeOrder={changeOrder}>
              שם
            </EmailListTableHeader>
            <EmailListTableHeader id="created" orderable
              order={order} changeOrder={changeOrder}>
              הצטרף ב
            </EmailListTableHeader>
            <EmailListTableHeader id="round" orderable
              order={order} changeOrder={changeOrder}
              width={200}>
              סיבוב מסרים
            </EmailListTableHeader>
            <EmailListTableHeader width={50}>
              פעולות
            </EmailListTableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedCustomers?.length ? orderedCustomers.map(customer=><TableRow key={customer.id}>
            <TableCell align="right">
              <Tooltip title={customer.unsubscribed&&'הלקוח הוסר מהשימת התפוצה'}>
                <Typography variant="body2" sx={customer.unsubscribed ? {textDecoration: 'line-through', color: '#CCC'} : {}}>
                  {customer.email}
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell align="right">{customer.name}</TableCell>
            <TableCell align="right">{formatTime(customer.created)}</TableCell>
            <TableCell align="right">{customer.round||'1'}</TableCell>
            <TableCell align="right">
              <Stack direction="1row" gap={2}>
                {!customer.eligible&&<SCButton text="הוספת סבב מסרים" icon={<AddCircleOutlineIcon/>}
                  variant="outlined" loading={addingRounds.has(customer.id)} size="small"
                  onClick={()=>openModal({
                      header: `לאשר עוד סבב מסרים עבור ${customer.name}?`,
                      content: <Stack direction="column" gap={2}>
                        <Typography>
                          לאחר האישור, המשתמש יוכל להיכנס שוב ללינק שלכם ולשלוף קלפי מסר לשבוע נוסף
                        </Typography>
                        <Stack direction="row" gap={2} justifyContent="end">
                          <SCButton text="ביטול" variant="outlined" onClick={closeModal}/>
                          <SCButton text="אישור" variant="contained" onClick={()=>{
                              addCustomerRound(customer);
                              closeModal();
                          }}/>
                        </Stack>
                      </Stack>,
                  })}/>}
              </Stack>
            </TableCell>
          </TableRow>) : <TableRow>
            <TableCell colSpan={5}>
              <EmailListEmptyState search={search}/>
            </TableCell>
          </TableRow>}
        </TableBody>
      </Table>
    </TableContainer>;
};

const EmailListTab = ()=>{
    let [customers, loadingCustomers] = useCustomers();
    let [search, setSearch] = useState('');
    let filteredCustomers = useMemo(()=>{
        if (!customers?.length||!search)
            return customers;
        return customers.filter(customer=>[customer.name, customer.email]
            .some(v=>v.toLowerCase().includes(search.toLowerCase())));
    }, [customers, search]);
    let [downloadEmails, downloadingEmails] = useBusyCallback(async ()=>{
        let resp = await api.tarotmail.student.downloadEmails();
        downloadBlob(resp.data, 'emails.csv');
    }, []);
    return <Stack direction="column" gap={2}>
      {!!customers?.length&&<Stack direction="row" gap={2} justifyContent="space-between" flexWrap="wrap">
        <Stack direction="row" gap={2} alignItems="center">
          <SCTextField size="small" placeholder="חיפוש" value={search} onChange={setSearch}/>
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          <SCButton variant="outlined" onClick={downloadEmails}
            loading={downloadingEmails} icon={<DownloadIcon size={20}/>}>
            הורדת רשימת מיילים
            </SCButton>
        </Stack>
      </Stack>}
      <EmailListTable customers={filteredCustomers} loading={loadingCustomers} search={search}/>
    </Stack>;
};

const SettingsTabSkeleton = ()=><Stack direction="column" gap={2}>
  <Skeleton variant="rounded" width="90%" height={50}/>
  <Skeleton variant="rounded" width="100%" height={50}/>
  <Stack direction="row" gap={2} justifyContent="space-between">
    <Skeleton variant="rounded" width={300} height={50}/>
    <Skeleton variant="rounded" width={200} height={50}/>
  </Stack>
</Stack>;

const CTA_TYPE_ITEMS = [
    {value: 'url', label: 'מעבר ללינק'},
    {value: 'wa', label: 'שליחת הודעת וואצאפ'},
    {value: 'phonecall', label: 'שיחת טלפון'},
    {value: 'email', label: 'שליחת מייל'},
];

const DEFAULT_CTA = {
    type: 'url',
    text: '',
    url: '',
    waPhone: '',
    waMessage: '',
    phonecallNum: '',
    emailAddr: '',
};

const CtaUrlComp = ({cta, setCta})=><>
  <SCTextField label="לינק" width="100%" sx={{flexGrow: 1}}
    value={cta.url} onChange={v=>setCta(prev=>({...prev, url: v}))}
    placeholder="יכול להיות אתר, מדיה או כל דבר"/>
</>;

const CtaWhatsappComp = ({cta, setCta})=><>
  <SCTextField type="tel"
    value={cta.waPhone} onChange={v=>setCta(prev=>({...prev, waPhone: v}))}
    label="טלפון"
    InputProps={{endAdornment: <Typography sx={{direction: 'ltr', paddingLeft: 2}}>+972</Typography>}}/>
  <SCTextField
    value={cta.waMessage} onChange={v=>setCta(prev=>({...prev, waMessage: v}))}
    label="הודעה שתופיע אוטומטית בוואצאפ (מהלקוח אליכם)"/>
</>;

const CtaPhoneComp = ({cta, setCta})=><>
  <SCTextField type="tel"
    value={cta.phonecallNum} onChange={v=>setCta(prev=>({...prev, phonecallNum: v}))}
    label="טלפון"
    InputProps={{endAdornment: <Typography sx={{direction: 'ltr', paddingLeft: 2}}>+972</Typography>}}/>
</>;

const CtaEmailComp = ({cta, setCta})=><>
  <SCTextField
    value={cta.emailAddr} onChange={v=>setCta(prev=>({...prev, emailAddr: v}))}
    label="כתובת מייל"/>
</>;

const SettingsTab = ()=>{
    let [user] = useUser();
    let [studentSettings, studentLoading] = useStudentSettings();
    let theme = useTheme();
    let [cta, setCta] = useState(DEFAULT_CTA);
    let {enqueueSnackbar} = useSnackbar();
    useEffect(()=>{
        if (studentSettings?.cta)
            setCta(studentSettings.cta);
    }, [studentSettings]);
    let queryClient = useQueryClient();
    let [sendTestMail, sendingTestMail] = useBusyCallback(async ()=>{
        await api.tarotmail.student.sendTestMail();
        enqueueSnackbar(`המייל נשלח בהצלחה ל-${user.email}`, {variant: 'success'});
    }, [enqueueSnackbar, user]);
    let [save, saving] = useBusyCallback(async ()=>{
        await api.tarotmail.student.updateSettings({cta});
        enqueueSnackbar('ההגדרות נשמרו בהצלחה', {variant: 'success'});
        queryClient.invalidateQueries({queryKey: ['student']});
    }, [queryClient, cta, enqueueSnackbar]);
    let CtaTypeComp = useMemo(()=>({
        url: CtaUrlComp,
        wa: CtaWhatsappComp,
        phonecall: CtaPhoneComp,
        email: CtaEmailComp,
    })[cta.type||'url']);
    if (studentLoading)
        return <SettingsTabSkeleton/>;
    return <Stack direction="column" gap={2}>
      <Typography variant="h5">הנעה לפעולה</Typography>
      <SCTextField label="הנעה לפעולה" multiline width="100%"
        value={cta.text} onChange={v=>setCta(prev=>({...prev, text: v}))}
        placeholder="הטקסט הזה יופיע בתחתית כל מייל, כדאי לכתוב משהו מזמין כמו הזמנה לשירות בהנחה או משהו כזה :)"/>
      <RadioCards label="בלחיצה על טקסט ההנעה לפעולה" value={cta.type} onChange={v=>setCta(prev=>({...prev, type: v}))}
        items={CTA_TYPE_ITEMS}/>
      <CtaTypeComp cta={cta} setCta={setCta}/>
      <Stack direction="row" gap={2} justifyContent="space-between">
        <SCButton variant="contained" onClick={save} loading={saving} icon={<CheckIcon size={20}/>}>
          שמירת הגדרות
        </SCButton>
        <SCButton variant="outlined" onClick={sendTestMail} loading={sendingTestMail} icon={<MailOutlineIcon size={20}/>}>
          שליחת מייל לבדיקה
        </SCButton>
      </Stack>
    </Stack>;
};

const TABS = {
    EMAIL_LIST: {
        label: 'רשימת משתמשים',
        Comp: EmailListTab,
    },
    SETTINGS: {
        label: 'הגדרות',
        Comp: SettingsTab,
    },
};

const CopyLinkButton = ()=>{
    let {enqueueSnackbar} = useSnackbar();
    let [user, loading] = useUser();
    let onClick = useCallback(()=>{
        let url = CONFIG.DOMAIN;
        if (user.id==SIMONA_USER_ID)
            url += 'tarotmail/simona';
        else
            url += 'tarotmailindex/'+user.id;
        navigator.clipboard.writeText(url);
        enqueueSnackbar('הלינק הועתק ללוח', {variant: 'success'});
    }, [enqueueSnackbar, user]);
    return <SCButton text="העתקת לינק למשתמשים" variant="contained"
      onClick={onClick} color="success"
      skeleton={loading} icon={<LinkIcon fontSize="small"/>}/>;
};

const TabsHeader = props=>{
    let {value, onChange} = props;
    let items = useMemo(()=>_.entries(TABS).map(([k, v])=>({value: k, label: v.label})), []);
    return <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap-reverse">
      <Box flexGrow={1} sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={value} onChange={(__, v)=>onChange(v)}>
          {items.map(item=><Tab key={item.value} {...item}/>)}]
        </Tabs>
      </Box>
      <Stack direction="row" gap={2}>
        <CopyLinkButton/>
      </Stack>
    </Stack>;
};

const TarotmailStudent = ()=>{
    let [tab, setTab] = useState(_.keys(TABS)[0]);
    let Comp = TABS[tab].Comp;
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">אפליקציית המסרים</Typography>
        <TabsHeader value={tab} onChange={setTab}/>
        <Comp/>
      </Stack>
    </Container>;
};

export default withTitle('אפליקציית המסרים', withMainContainer(
    withLoginRequire(withPermGuard(TarotmailStudent, 'tarotmail.student'))));
